import React, { useEffect, useState } from "react";
import clsx from "clsx";

import { ProductSingleModule } from "@app/api/modules/product-single/product-single.module";
import { BreadcrumbComponent } from "@app/core/breadcrumb";
import { ImageComponent } from "@app/core";
import {
  DealCardLandscape,
  getAffiliateDealLinks
} from "@app/components/deal-cards/deal-card-landscape/deal-card-landscape";
import { RelAttributes } from "@app/constants";
import { useIsClamped } from "@app/util/hooks/use-is-clamped";
import { formatMessage } from "@app/translations/intl";

import styles from "./product-single-component.module.scss";
import { HtmlRenderComponent } from "@app/core/html-render";

export interface IProductSingleProps {
  module: ProductSingleModule;
}

export const ProductSingle = (props: IProductSingleProps) => {
  const { product, breadcrumbProps, compareTitle, noResultText } = props.module;

  const noResults = !product.deals || product.deals?.length === 0;

  // Title and description are always in the DOM in full for SEO; the clamps are visual only.
  const [expanded, setExpanded] = useState(false);
  const [titleRef, titleIsClamped] = useIsClamped<HTMLHeadingElement>(styles.titleClamped, product.title);
  const [descriptionRef, descriptionIsClamped] = useIsClamped<HTMLDivElement>(
    styles.descriptionClamped,
    product.description
  );
  const showToggle = titleIsClamped || descriptionIsClamped;

  // Client-side navigation between products reuses this component, because the page builder keys
  // its modules by index. Without this the next product opens with the previous one unfolded.
  useEffect(() => setExpanded(false), [product.id]);

  const controlledIds = [product.title && "product-title", product.description && "product-description"]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="uk-container">
      <div className={clsx(styles.header)}>
        {breadcrumbProps && (
          <BreadcrumbComponent
            variant={breadcrumbProps.variant}
            breadcrumbs={breadcrumbProps.breadcrumbs}
            backButton={breadcrumbProps.backButton}
          />
        )}
        {product.title && (
          <h1 id="product-title" ref={titleRef} className={clsx(!expanded && styles.titleClamped)}>
            {product.title}
          </h1>
        )}
        {product.description && (
          // A div, not a p: the CMS description brings its own block tags, and a p inside a p is
          // invalid HTML that the browser parser splits apart.
          <div
            id="product-description"
            ref={descriptionRef}
            className={clsx(styles.description, !expanded && styles.descriptionClamped)}
          >
            <HtmlRenderComponent html={product.description} />
          </div>
        )}
        {showToggle && (
          <button
            type="button"
            className={styles.descriptionToggle}
            aria-expanded={expanded}
            aria-controls={controlledIds}
            onClick={() => setExpanded(!expanded)}
          >
            {formatMessage({ id: expanded ? "productSingle.readLess" : "productSingle.readMore" })}
          </button>
        )}
      </div>
      <div className={styles.details}>
        <CardImage {...props} />
        <div className={styles.compare}>
          {compareTitle && <h2>{compareTitle}</h2>}
          <div className={styles.list}>
            {!noResults ? (
              product.deals?.map((deal) => (
                <DealCardLandscape key={deal.id} platformId={product.platform.id} {...deal} />
              ))
            ) : (
              <p>{noResultText}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CardImage = (props: IProductSingleProps) => {
  const { product } = props.module;
  const { outDealLink } = getAffiliateDealLinks(product.deals?.[0]?.id, product.deals?.[0]?.merchant?.slug);

  const img = (
    <ImageComponent
      forceImageTag
      alt={product.image?.alt || ""}
      src={product.image?.url || ""}
      title={product.image?.title || ""}
      isBlocking
    />
  );

  if (!product.image?.url) {
    return <></>;
  }

  if (outDealLink) {
    return (
      <a className={styles.image} rel={RelAttributes.SPONSORED} target="_blank" href={outDealLink}>
        {img}
      </a>
    );
  }

  return <div className={styles.image}>{img}</div>;
};
